// Forget everything this browser has learned about the player.
//
// The curriculum (§16) offers a concept once and then stops, which is exactly
// right for a player and exactly wrong for anybody *building* it: the second
// time you open the hint you were working on, it is gone, and the only way back
// was to find the key in devtools and clear it by hand.
//
// A dev control rather than a command, and deliberately not in `SOLO_COMMANDS`:
// the profile is not game state and never reaches the log. `src/engine/rules/` cannot
// see it, so there is nothing here for a replay to reproduce — which is the
// same property that lets a player edit it themselves without consequence.

import { useState } from "react";

import { loadProfile, resetProfile } from "src/engine/telemetry/profile";
import { Btn, HoverCard } from "../kit";
import { useBesidePanel, Fold } from "./parts";

export function ResetOnboarding() {
  const [cleared, setCleared] = useState(false);
  const profile = loadProfile();
  const met = Object.values(profile.counts).reduce((n, count) => n + (count ?? 0), 0);
  const waved = profile.dismissed?.length ?? 0;
  const panel = useBesidePanel();

  return (
    <Fold id="onboarding" title="Onboarding">
      <HoverCard
        placement="right"
        beside={panel}
        title="Reset onboarding"
        card="Gives back every hint waved away with the ×, and forgets what this browser has used."
      >
        <Btn
          small
          data-testid="dev-reset-onboarding"
          // **Live, not "reload to see it fresh".** A dismissal is permanent
          // now, so this is the only way back — and a way back that needs a
          // page reload is one nobody uses while building the thing it exists
          // for. `resetProfile` tells `useHints`, which is holding its own copy.
          onClick={() => {
            resetProfile();
            setCleared(true);
          }}
        >
          {cleared
            ? "Onboarding forgotten — hints are back"
            : `Reset onboarding (${met} uses, ${waved} dismissed, ${profile.games} games)`}
        </Btn>
      </HoverCard>
    </Fold>
  );
}
